import { CandidateSummaryPayload } from '../models/candidate-summary.model';
import {
  ClarificationQuestion,
  ClarificationQuestionSource,
} from '../models/clarification.model';
import { normalizeAssessmentGaps } from './candidate-summary-gaps';

export interface ClarificationQuestionSuggestion {
  question: string;
  source: ClarificationQuestionSource;
}

export function buildFollowUpQuestionSuggestion(
  originalQuestion: string,
  answer?: string
): ClarificationQuestionSuggestion {
  const question = ensureQuestion(originalQuestion);
  const action = answer?.trim()
    ? `expand on your answer to “${question}”`
    : `answer “${question}”`;
  return {
    question: `Could you ${action} with a specific example that includes your role, timeframe, and outcome?`,
    source: 'MANUAL',
  };
}

export function buildFollowUpQuestionSuggestions(
  questions: ClarificationQuestion[]
): ClarificationQuestionSuggestion[] {
  return [...questions]
    .filter(
      (question) =>
        question.reviewStatus === 'FOLLOW_UP_NEEDED' && question.answer?.trim()
    )
    .sort((first, second) => first.position - second.position)
    .map((question) =>
      buildFollowUpQuestionSuggestion(question.question, question.answer)
    )
    .slice(0, 5);
}

function ensureQuestion(text: string): string {
  const normalized = text.trim().replace(/[?!.]+$/, '');
  if (!normalized) return '';
  return `${normalized}?`;
}

function lowercaseFirst(value: string): string {
  if (
    value.length > 1 &&
    value[0] === value[0].toUpperCase() &&
    value[1] === value[1].toUpperCase()
  ) {
    return value;
  }
  return value.charAt(0).toLowerCase() + value.slice(1);
}

function removeRecruiterDirection(text: string): string {
  return text
    .trim()
    .replace(/[?!.]+$/, '')
    .replace(
      /^(?:please\s+)?(?:ask(?:\s+(?:the\s+)?candidate)?\s+(?:about|to)|validate|clarify|confirm|verify|explore|assess|evaluate|determine|understand|discuss|probe(?:\s+for)?|inquire(?:\s+about)?)\s+/i,
      ''
    )
    .replace(
      /^(?:missing|insufficient|limited|unclear|no)\s+(?:evidence|detail|details|information)(?:\s+(?:for|of|about|on))?\s+/i,
      ''
    )
    .replace(/^whether\s+(?:(?:the\s+)?candidate|they)\s+/i, 'whether you ')
    .replace(/^(?:the\s+)?candidate['’]s\s+/i, 'your ')
    .replace(/^their\s+/i, 'your ')
    .replace(/^they\s+/i, 'you ')
    .trim();
}

function candidatePossessiveTopic(value: string): string {
  const normalized = lowercaseFirst(value);
  return /^(?:your|the|a|an|this|that|any|whether)\b/i.test(normalized)
    ? normalized
    : `your ${normalized}`;
}

function asCandidateQuestion(
  text: string,
  kind: 'GAP' | 'INTERVIEW_FOCUS'
): string {
  const original = text.trim();
  if (!original) return '';
  const normalized = removeRecruiterDirection(original);
  if (!normalized) return '';
  if (/^whether\b/i.test(normalized)) {
    return `Could you tell us ${ensureQuestion(normalized)}`;
  }
  if (
    /^(?:how|what|when|where|why|which|who|could|can|would|did|do|does|have|has|are|is|was|were)\b/i.test(
      normalized
    )
  ) {
    return ensureQuestion(normalized);
  }
  if (
    /^(?:describe|explain|share|provide|tell\s+us|walk\s+us\s+through)\b/i.test(
      normalized
    )
  ) {
    return `Could you ${ensureQuestion(lowercaseFirst(normalized))}`;
  }
  const topic = candidatePossessiveTopic(normalized);
  return kind === 'GAP'
    ? `Could you share a specific example that demonstrates ${topic}?`
    : `Could you tell us more about ${topic}?`;
}

export function buildClarificationQuestionSuggestions(
  payload: CandidateSummaryPayload,
  source: ClarificationQuestionSource
): ClarificationQuestionSuggestion[] {
  if (source === 'AI_BRIEF_GAP') {
    return normalizeAssessmentGaps(payload)
      .filter((gap) => gap.status === 'OPEN')
      .map((gap) => ({
        question: asCandidateQuestion(gap.description, 'GAP'),
        source,
      }))
      .filter((item) => item.question)
      .slice(0, 5);
  }

  if (source === 'AI_BRIEF_INTERVIEW_FOCUS') {
    return (payload.interviewFocus ?? [])
      .map((focus) => ({
        question: asCandidateQuestion(focus, 'INTERVIEW_FOCUS'),
        source,
      }))
      .filter((item) => item.question)
      .slice(0, 5);
  }

  return [];
}
